import { useEffect, useState } from 'react';
import {
  cardLabel,
  DECK_IDS,
  DECKS,
  isDeckId,
  type CompletedRound,
  type DeckId,
  type PublicRoom,
  type Role,
} from '@planning-session/shared';
import { History } from './History.js';
import { HostControls } from './HostControls.js';
import { StoryQueue } from './StoryQueue.js';
import { ChevronIcon } from './ui/Icon.js';
import styles from './SessionAside.module.css';

export interface SessionAsideProps {
  readonly room: PublicRoom;
  readonly isHost: boolean;
  readonly isPlayer: boolean;
  onDeck: (deckId: DeckId) => void;
  onRole: (role: Role) => void;
  onQueue: (titles: string[]) => void;
  onRemoveQueued: (storyId: string) => void;
  onAutoReveal: (enabled: boolean) => void;
  onTransferHost: (participantId: string) => void;
  onRemoveParticipant: (participantId: string) => void;
}

/**
 * The session read back as plain text. Results are entered into the tracker by
 * hand, so the log is written to be pasted there rather than re-typed.
 */
function asText(roomId: string, rounds: readonly CompletedRound[]): string {
  const lines = rounds.map((round, index) => {
    const title = round.title || 'Untitled story';
    const verdict = round.summary.consensus
      ? `agreed ${cardLabel(round.summary.distribution[0]?.value ?? '—')}`
      : `no consensus, average ${round.summary.average ?? '—'}`;
    const cards = round.summary.distribution.map((tally) => `${cardLabel(tally.value)}×${tally.count}`).join(' ');
    return `${index + 1}. ${title} — ${verdict} (${cards})`;
  });

  return [`Planning session ${roomId}`, ...lines].join('\n');
}

/**
 * The room's second column: what this session has decided, and the two controls
 * that change how it runs. The record leads; the controls are utility and carry
 * no panel of their own.
 */
export function SessionAside({
  room,
  isHost,
  isPlayer,
  onDeck,
  onRole,
  onQueue,
  onRemoveQueued,
  onAutoReveal,
  onTransferHost,
  onRemoveParticipant,
}: SessionAsideProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1_800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const sized = room.history.length;
  const agreed = room.history.filter((round) => round.summary.consensus).length;

  // Spelled out rather than drawn: the icon family does not grow for a hint, and
  // a unicode glyph standing in for a key cap is exactly what it forbids.
  const keyHints = [
    ...(isPlayer ? ['1–9 play a card', 'Backspace takes it back'] : []),
    ...(isHost ? ['R reveals', 'N next story'] : []),
  ];

  async function copyLog() {
    try {
      await navigator.clipboard.writeText(asText(room.id, room.history));
      setCopied(true);
    } catch {
      // Nothing useful to say; the log is on screen.
    }
  }

  return (
    <aside className={styles.aside}>
      <StoryQueue queue={room.queue} isHost={isHost} onQueue={onQueue} onRemove={onRemoveQueued} />

      <section className={styles.record} aria-label="Session record">
        <div className={styles.figures}>
          <span className={styles.figure}>
            <span className={styles.figureValue}>{sized}</span>
            <span className={styles.figureLabel}>{sized === 1 ? 'Story sized' : 'Stories sized'}</span>
          </span>
          <span className={styles.figure}>
            <span className={`${styles.figureValue} ${agreed > 0 ? styles.figureAgreed : ''}`}>{agreed}</span>
            <span className={styles.figureLabel}>Agreed</span>
          </span>
        </div>

        {sized === 0 ? (
          <p className={styles.empty}>
            Nothing sized yet. Each finished round lands here with its cards, ready to copy into your tracker.
          </p>
        ) : (
          <>
            <History rounds={room.history} />
            <button type="button" className={`${styles.copy} ${copied ? styles.copied : ''}`} onClick={copyLog}>
              {copied ? 'Copied to clipboard' : 'Copy log for the tracker'}
            </button>
          </>
        )}
      </section>

      <div className={styles.controls}>
        <h2 className={styles.controlsTitle}>Settings</h2>

        <div className={styles.control}>
          <span className={styles.controlLabel}>Deck</span>
          {isHost ? (
            <span className={styles.selectWrap}>
              <select
                className={styles.select}
                value={room.deck.id}
                aria-label="Deck"
                onChange={(event) => {
                  if (isDeckId(event.target.value)) onDeck(event.target.value);
                }}
              >
                {DECK_IDS.map((id) => (
                  <option key={id} value={id}>
                    {DECKS[id].label}
                  </option>
                ))}
              </select>
              <span className={styles.selectChevron}>
                <ChevronIcon size={14} />
              </span>
            </span>
          ) : (
            <span className={styles.controlValue}>{room.deck.label}</span>
          )}
        </div>

        <div className={styles.control}>
          <span className={styles.controlLabel}>Auto-reveal</span>
          {isHost ? (
            <span className={styles.roles} role="group" aria-label="Auto-reveal">
              {([false, true] as const).map((option) => (
                <button
                  key={String(option)}
                  type="button"
                  className={`${styles.role} ${room.autoReveal === option ? styles.roleActive : ''}`}
                  aria-pressed={room.autoReveal === option}
                  onClick={() => onAutoReveal(option)}
                >
                  {option ? 'On' : 'Off'}
                </button>
              ))}
            </span>
          ) : (
            // Worth showing to everyone: it changes what happens when you play.
            <span className={styles.controlValue}>{room.autoReveal ? 'On' : 'Off'}</span>
          )}
        </div>

        <div className={styles.control}>
          <span className={styles.controlLabel}>You</span>
          <span className={styles.roles} role="group" aria-label="Your role">
            {(['player', 'observer'] as const).map((option) => {
              const active = isPlayer === (option === 'player');
              return (
                <button
                  key={option}
                  type="button"
                  className={`${styles.role} ${active ? styles.roleActive : ''}`}
                  aria-pressed={active}
                  onClick={() => onRole(option)}
                >
                  {option === 'player' ? 'Player' : 'Observer'}
                </button>
              );
            })}
          </span>
        </div>

        {keyHints.length > 0 && (
          <div className={styles.control}>
            <span className={styles.controlLabel}>Keys</span>
            <span className={styles.keys}>{keyHints.join(' · ')}</span>
          </div>
        )}

        {isHost && (
          <HostControls
            participants={room.participants}
            youId={room.you?.id ?? null}
            onTransferHost={onTransferHost}
            onRemoveParticipant={onRemoveParticipant}
          />
        )}
      </div>
    </aside>
  );
}
